import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Typography,
  Slider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Link,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  PlayArrow as PlayArrowIcon,
  Pause as PauseIcon,
  Flip as FlipIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Settings as SettingsIcon,
  RestartAlt as RestartAltIcon,
  TextDecrease as TextDecreaseIcon,
  TextIncrease as TextIncreaseIcon,
  KeyboardDoubleArrowLeft as ArrowLeftIcon,
  KeyboardDoubleArrowRight as ArrowRightIcon,
} from '@mui/icons-material';
import { styled } from '@mui/system';
import queryString from 'query-string';

const FONT_SIZE_OPTIONS = [10, 12, 14, 16, 18, 20, 24, 28, 32, 36, 40, 46, 52, 58, 64, 70, 76, 82, 88, 94, 100];

const Container = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  height: '100vh',
}));

const MainContent = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  height: '100%',
}));

const TeleprompterOutput = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
  marginTop: '-16px',
  marginBottom: '16px',
  maxHeight: 'calc(100vh - 32px)',
}));


const MenuContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifySelf: 'start',
}));

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  width: '100%',
}));

const summaryStyle = {
  minHeight: '0px',
  padding: '0px',
  visibility: 'hidden',
  display: 'none',
};

const flipText = (text) => {
  const words = text.split(' ');
  const reversedWords = words.reverse();
  const reversedText = reversedWords.join(' ');
  return reversedText;
};

const getParagraphs = (str) => {
  if (!str) return [];
  return str.split(/\n\s*\n/);
};

const breakIntoWords = (str) => {
  const paragraphs = getParagraphs(str);
  const words = paragraphs.map((paragraph) => paragraph.split(/\s+/)).flat();
  return words.filter((word) => word !== '');
};

const WPM = 180; // Average for english in united states is 150. My videos are closer to 188.

const getEstimatedDuration = (str) => {
  const wordsPerMinute = WPM;
  const words = breakIntoWords(str);
  const totalMinutes = words.length / wordsPerMinute;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = Math.floor(totalMinutes % 60);
  const seconds = Math.round((totalMinutes % 1) * 60);
  let result = '';
  if (hours > 0) {
    result += `${hours} hour${hours > 1 ? 's' : ''} `;
  }
  if (minutes > 0) {
    result += `${minutes} minute${minutes > 1 ? 's' : ''} `;
  }
  if (seconds > 0) {
    result += `${seconds} second${seconds > 1 ? 's' : ''}`;
  }
  return result.trim();
};



const renderTextWithBold = (text, matchedIndex, isFlipped) => {
  const words = text.split(' ');
  return words.map((word, index) => {
    const displayWord = isFlipped ? flipText(word) : word;
    if (index === matchedIndex) {
      return <b key={index}>{displayWord} </b>;
    } else {
      return <span key={index}>{displayWord} </span>;
    }
  });
};

const Teleprompter = () => {
  const query = queryString.parse(window.location.search);
  // Persisted
  const [scrollSpeed, setScrollSpeed] = useState(query.scrollSpeed ? Number(query.scrollSpeed) : 5);
  const [isFlipped, setIsFlipped] = useState((query.isFlipped == null || query.isFlipped === 'true') ? true : false);
  const [sidePadding, setSidePadding] = useState(query.sidePadding ? Number(query.sidePadding) : 16);
  const [fontSizeIndex, setFontSizeIndex] = useState(query.fontSizeIndex ? Number(query.fontSizeIndex) : 6);
  const [fontFamily, setFontFamily] = useState(query.fontFamily || 'Arial');
  const [backgroundColor, setBackgroundColor] = useState(query.backgroundColor || '#000000');
  const [textColor, setTextColor] = useState(query.textColor || '#ffffff');
  // Not persisted
  const [expanded, setExpanded] = useState(true);
  const [isScrolling, setIsScrolling] = useState(false);
  const [inputText, setInputText] = useState('');
  const [matchedWordIndex, setMatchedWordIndex] = useState(-1); 
  const fontSize = FONT_SIZE_OPTIONS[fontSizeIndex];
  const wordCount = breakIntoWords(inputText).length;
  const estimatedMinutes = getEstimatedDuration(inputText);

  const scrollInterval = useRef(null);
  const outputRef = useRef(null);

  useEffect(() => {
    const newQuery = { ...query, scrollSpeed };
    window.history.pushState(null, '', '?' + queryString.stringify(newQuery));
  }, [scrollSpeed]);

  useEffect(() => {
    const newQuery = { ...query, isFlipped };
    window.history.pushState(null, '', '?' + queryString.stringify(newQuery));
  }, [isFlipped]);

  useEffect(() => {
    const newQuery = { ...query, sidePadding };
    window.history.pushState(null, '', '?' + queryString.stringify(newQuery));
  }, [sidePadding]);

  useEffect(() => {
    const newQuery = { ...query, fontSizeIndex };
    window.history.pushState(null, '', '?' + queryString.stringify(newQuery));
  }, [fontSizeIndex]);

  useEffect(() => {
    const newQuery = { ...query, fontFamily };
    window.history.pushState(null, '', '?' + queryString.stringify(newQuery));
  }, [fontFamily]);

  useEffect(() => {
    const newQuery = { ...query, backgroundColor };
    window.history.pushState(null, '', '?' + queryString.stringify(newQuery));
  }, [backgroundColor]);

  useEffect(() => {
    const newQuery = { ...query, textColor };
    window.history.pushState(null, '', '?' + queryString.stringify(newQuery));
  }, [textColor]);

  const handleChange = (e) => {
    setInputText(e.target.value);
  };

  const scrollContent = () => {
    if (outputRef.current) {
      outputRef.current.scrollTop += 1; // we always want to scroll one px at a time.
    }
  };

  const startScroll = () => {
    setIsScrolling(true);
    if (scrollInterval.current) {
      clearInterval(scrollInterval.current);
    }
    scrollInterval.current = setInterval(scrollContent, 200 / scrollSpeed); // Call scrollContent every 10ms
  };

  const pauseScroll = () => {
    setIsScrolling(false);
    clearInterval(scrollInterval.current);
  };

  const stopScroll = () => {
    setIsScrolling(false);
    clearInterval(scrollInterval.current);
    if (outputRef.current) {
      outputRef.current.scrollTop = 0;
    }
  };

  const getPositionForPercentage = (percentage, containerHeight) => {
    return (containerHeight * percentage) / 100;
  };

  const jumpToPreviousParagraph = () => {
    pauseScroll();
    const paragraphs = outputRef.current.getElementsByTagName('p');
    let currentTop = outputRef.current.scrollTop;
    let prevParagraphTop = 0;

    if (currentTop === 0) {
      outputRef.current.scrollTop = 0;
      return;
    }

    let cumulativeHeight = 0;
    for (let i = 0; i < paragraphs.length; i++) {
      const paragraphHeight = (paragraphs[i].clientHeight) + 16;
      if (cumulativeHeight + paragraphHeight > currentTop) {
        outputRef.current.scrollTop = prevParagraphTop;
        break;
      }
      prevParagraphTop = cumulativeHeight;
      cumulativeHeight += paragraphHeight;
    }
  };

  const jumpToNextParagraph = () => {
    pauseScroll();
    const paragraphs = outputRef.current.getElementsByTagName('p');
    let currentTop = outputRef.current.scrollTop;

    let cumulativeHeight = 0;
    for (let i = 0; i < paragraphs.length; i++) {
      const paragraphHeight = (paragraphs[i].clientHeight) + 16;

      if (currentTop === 0) {
        outputRef.current.scrollTop = paragraphHeight;
        return;
      }

      if (cumulativeHeight + paragraphHeight > currentTop) {
        cumulativeHeight += paragraphHeight;
        outputRef.current.scrollTop = cumulativeHeight;
        break;
      }
      cumulativeHeight += paragraphHeight;
    }
  };

  const jumpBackTwoParagraphsAndStartScroll = () => {
    jumpToPreviousParagraph();
    jumpToPreviousParagraph();
    setTimeout(() => {
      startScroll();
    }, 200);
  };

  const handleAccordionToggle = () => {
    setExpanded(!expanded);
    pauseScroll();
  };

  const increasePadding = () => {
    setSidePadding(sidePadding + 8);
  };

  const decreasePadding = () => {
    let nextSidePadding = sidePadding - 8;
    if (nextSidePadding < 16) {
      nextSidePadding = 16;
    }
    if (nextSidePadding !== sidePadding) {
      setSidePadding(nextSidePadding);
    }
  };

  const onSelectFontSize = size => {
    let nextFontSizeIndex = FONT_SIZE_OPTIONS.indexOf(size);
    if (nextFontSizeIndex !== fontSizeIndex) {
      setFontSizeIndex(nextFontSizeIndex);
    }
  };

  const increaseFont = () => {
    const max = FONT_SIZE_OPTIONS.length - 1;
    let nextFontSizeIndex = fontSizeIndex + 1;
    if (nextFontSizeIndex > max) {
      nextFontSizeIndex = max;
    }
    setFontSizeIndex(nextFontSizeIndex);
  };

  const decreaseFont = () => {
    let nextFontSizeIndex = fontSizeIndex - 1;
    if (nextFontSizeIndex < 0) {
      nextFontSizeIndex = 0;
    }
    if (nextFontSizeIndex !== fontSizeIndex) {
      setFontSizeIndex(nextFontSizeIndex);
    }
  };

  const LeftRailWrapper = () => {
    return (
      <Box
        sx={{
          width: '50px',
          top: 0,
          left: 0,
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
      >
        {
          expanded &&
          <Box>
            <IconButton onClick={handleAccordionToggle}>
              <CheckCircleIcon color='primary' />
            </IconButton>
          </Box>
        }
        {!expanded &&
          <Box>
            <IconButton onClick={handleAccordionToggle}>
              <SettingsIcon />
            </IconButton>
            <IconButton onClick={increaseFont}>
              <TextIncreaseIcon />
            </IconButton>
            <IconButton onClick={decreaseFont}>
              <TextDecreaseIcon />
            </IconButton>
            <IconButton
              onClick={() => setIsFlipped(!isFlipped)}>
              <FlipIcon />
            </IconButton>
            <IconButton onClick={increasePadding}>
              <ArrowRightIcon />
            </IconButton>
            <IconButton onClick={decreasePadding}>
              <ArrowLeftIcon />
            </IconButton>
          </Box>
        }

      </Box>
    );
  };

  const RightRailWrapper = () => {
    return (
      <Box
        sx={{
          width: '50px',
          top: 0,
          right: 0,
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
      >
        {!expanded &&
          <Box>
            {isScrolling &&
              <IconButton onClick={pauseScroll}>
                <PauseIcon />
              </IconButton>
            }
            {!isScrolling &&
              <IconButton onClick={startScroll}>
                <PlayArrowIcon />
              </IconButton>
            }


            <IconButton onClick={jumpBackTwoParagraphsAndStartScroll}>
              <RestartAltIcon />
            </IconButton>
            <IconButton onClick={() => {
              setTimeout(() => {
                jumpToPreviousParagraph();
              }, 100);
            }}>
              <ArrowUpwardIcon />
            </IconButton>
            <IconButton onClick={() => {
              setTimeout(() => {
                jumpToNextParagraph();
              }, 100);
            }}>
              <ArrowDownwardIcon />
            </IconButton>
          </Box>
        }
      </Box>
    );
  };

  const recognition = new (window.SpeechRecognition || window.webkitSpeechRecognition || window.mozSpeechRecognition || window.msSpeechRecognition)();

  recognition.continuous = true;
  recognition.interimResults = true;
  recognition.lang = 'en-US';

  recognition.onresult = (event) => {
    const transcript = Array.from(event.results)
      .map((result) => result[0])
      .map((result) => result.transcript)
      .join('');

    const words = inputText.split(' ');
    const transcriptWords = transcript.split(' ');
    let matchedIndex = -1;

    // Find the index of the last word in the input text that matches the last word in the transcript
    for (let i = words.length - 1; i >= 0; i--) {
      if (words[i] === transcriptWords[transcriptWords.length - 1]) {
        matchedIndex = i;
        break;
      }
    }

    if (matchedIndex > -1) {
      setMatchedWordIndex(matchedIndex);

      const matchedText = words.slice(0, matchedIndex + 1).join(' ');

      // Calculate the scroll position based on the matchedIndex and lineHeight
      const lineHeight = fontSize * 1.5; // Adjust based on your CSS
      const textLines = matchedText.split('\n');
      let scrollTopPosition = 0;

      textLines.forEach((line, index) => {
        const wordsInLine = line.split(' ');
        scrollTopPosition += wordsInLine.length * lineHeight;
      });

      outputRef.current.scrollTop = scrollTopPosition;
    }
  };

  recognition.onerror = (event) => {
    console.error('Speech recognition error:', event);
  };

  useEffect(() => {
    recognition.onstart = () => {
      console.log('Speech recognition started');
    };

    recognition.onend = () => {
      console.log('Speech recognition ended');
      recognition.start(); // Restart recognition when it stops
    };
    if (!recognition.start) {
      recognition.start();
    }
    return () => {
      recognition.stop();
    };
  }, [isScrolling]);

  const handleRemoteButton = (event) => {
    // Implement Bluetooth remote button handling here
  };

  useEffect(() => {
    // Set up Bluetooth remote event listener here
    // Example: window.addEventListener('remotebutton', handleRemoteButton);

    return () => {
      // Clean up Bluetooth remote event listener here
      // Example: window.removeEventListener('remotebutton', handleRemoteButton);
    };
  }, []);

  return (
    <Container color="white" >
      <LeftRailWrapper />
      <MainContent>
        <Box sx={{
          paddingTop: '16px',
          paddingBottom: '16px',
        }}>
          <StyledAccordion expanded={expanded} disableGutters>
            <AccordionSummary color="inherit" style={summaryStyle}>
              <Typography></Typography>
            </AccordionSummary>
            <AccordionDetails sx={{
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
            }}>

              <Box flexGrow={1}>
                <Typography color="inherit">Free Online Teleprompter</Typography>
                <Box my={2}>
                  <TextField
                    fullWidth
                    multiline
                    rows={8}
                    label="Input Text"
                    variant="outlined"
                    value={inputText}
                    onChange={handleChange}
                  />
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      fontSize: '10px',
                      padding: '4px 8px'
                    }}
                  >
                    <Typography fontSize={'12px'} color="inherit">Video duration ~ {estimatedMinutes} for {wordCount} words @ {WPM} WPM</Typography>
                  </Box>
                </Box>
                <Box my={2}>
                  <Typography variant="subtitle1">Scroll Speed: {scrollSpeed}</Typography>
                  <Slider
                    value={scrollSpeed}
                    onChange={(e, value) => setScrollSpeed(value)}
                    min={1}
                    max={100}
                    step={1}
                    valueLabelDisplay="auto"
                  />
                </Box>

                <Box my={2}>
                  <FormControl fullWidth>
                    <InputLabel htmlFor="font-size-input">Font Size</InputLabel>
                    <Select
                      id="font-size-input"
                      value={fontSize}
                      onChange={(e) => onSelectFontSize(parseInt(e.target.value))}
                      label="Font Size"
                    >
                      {FONT_SIZE_OPTIONS.map((size) => (
                        <MenuItem key={size} value={size}>
                          {size}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                <Box my={2}>
                  <TextField
                    fullWidth
                    select
                    label="Font Family"
                    value={fontFamily}
                    onChange={(e) => setFontFamily(e.target.value)}
                  >
                    {['Arial', 'Courier', 'Georgia', 'Tahoma', 'Times New Roman'].map((font) => (
                      <MenuItem key={font} value={font}>
                        {font}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
                <Box my={2} >
                  <TextField
                    label="Background Color"
                    type="color"
                    value={backgroundColor}
                    onChange={(e) => setBackgroundColor(e.target.value)}
                    InputProps={{
                      style: { width: `${200}px`, margin: ' 0 8px 24px 8px' },
                    }}
                  />
                  <TextField
                    label="Text Color"
                    type="color"
                    value={textColor}
                    onChange={(e) => setTextColor(e.target.value)}
                    InputProps={{
                      style: { width: `${200}px`, margin: ' 0 8px 24px 8px' },
                    }}
                  />
                </Box>
              </Box>
              <Box
                flexGrow={1}
                sx={{
                  position: 'relative',
                  height: '100%',
                  maxHeight: '100%',
                }}
              >
                <Typography fontSize={'12px'} color="inherit">Teleprompter App Created by <Link
                  href="https://jamescrossjr.com/"
                  style={{ color: '#FFFFFF' }}
                  underline="always"
                >
                  James Cross
                </Link> with ChatGPT
                </Typography>
              </Box>
            </AccordionDetails>
          </StyledAccordion>
        </Box>
        {!expanded &&
          <TeleprompterOutput>
            <Box
              ref={outputRef}
              sx={{
                transform: isFlipped ? 'scale(1, -1)' : 'scale(1, 1)',
                fontSize: fontSize,
                fontFamily: fontFamily,
                color: textColor,
                backgroundColor: backgroundColor,
                lineHeight: `${fontSize + 4}px`,
                paddingTop: '16px',
                paddingBottom: '16px',
                paddingLeft: `${sidePadding}px`,
                paddingRight: `${sidePadding}px`,
                border: '1px solid #444',
                borderRadius: '4px',
                overflowY: 'scroll',
                height: '100%',
                maxHeight: '100%',
                whiteSpace: 'pre-wrap',
                overflowWrap: 'anywhere',
              }}
            >
              {getParagraphs(inputText).map((paragraph, index) => (
                <p key={index}>{paragraph}</p>
              ))}
            </Box>
          </TeleprompterOutput>
        }
      </MainContent>
      <RightRailWrapper />
    </Container>
  );
};

export default Teleprompter;
